import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  api, listEnrollments, approveEnrollment, revokeEnrollment, resetEnrollmentSecret,
  type Asset, type GatewayEnrollment,
} from "../api/rest";
import { useSiteId } from "../hooks/useSiteId";
import css from "./WarehouseAdminPage.module.css";

/** Device enrollment / quarantine admin (decisions.md#69). An edge appears here as
 *  "pending" on first contact; an administrator binds it to an asset to bring it online.
 *  The asset is picked from a list — a free-typed id once deafened a whole command
 *  channel by binding the gateway to the wrong forklift. */
export function DevicesAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const siteId = useSiteId();
  const { data: rows = [] } = useQuery({
    queryKey: ["enrollments"],
    queryFn: () => listEnrollments(),
    refetchInterval: 5000,           // surface a newly-booted device without a manual refresh
  });
  // edgeBound comes from the same source of truth the server gates mapping on, so
  // already-bound assets drop out of the picker automatically.
  const { data: assets = [] } = useQuery({
    queryKey: ["assets", siteId],
    queryFn: () => api.listAssets(siteId),
    enabled: !!siteId,
  });
  const free = assets.filter((a) => !a.edgeBound);

  const approve = useMutation({
    mutationFn: ({ hw, assetId }: { hw: string; assetId: string }) => approveEnrollment(hw, assetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrollments"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (hw: string) => revokeEnrollment(hw),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrollments"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
  // Reflashed-unit recovery (ADR 135): clears the pinned secret, back to pending.
  const resetSecret = useMutation({
    mutationFn: (hw: string) => resetEnrollmentSecret(hw),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrollments"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  const pending = rows.filter((r) => r.status === "pending");

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.title}>{t("nav.devices")}</div>
        {pending.length > 0 && (
          <span className={css.siteTag}>{t("devicesAdmin.pending", { count: pending.length })}</span>
        )}
      </div>

      {approve.isError && (
        <p className={css.errorMsg}>{(approve.error as Error).message}</p>
      )}

      <table className={css.table}>
        <thead>
          <tr>
            <th>{t("devicesAdmin.col.hardwareId")}</th><th>{t("common.col.status")}</th><th>{t("common.col.asset")}</th>
            <th>{t("devicesAdmin.col.firstSeen")}</th><th>{t("common.col.lastSeen")}</th><th>{t("common.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <DeviceRow key={e.hardwareId} e={e} freeAssets={free}
              onApprove={(assetId) => approve.mutate({ hw: e.hardwareId, assetId })}
              onRevoke={() => revoke.mutate(e.hardwareId)}
              onResetSecret={() => resetSecret.mutate(e.hardwareId)} />
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6}>{t("devicesAdmin.empty")}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DeviceRow({ e, freeAssets, onApprove, onRevoke, onResetSecret }: {
  e: GatewayEnrollment;
  freeAssets: Asset[];
  onApprove: (assetId: string) => void;
  onRevoke: () => void;
  onResetSecret: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [choosing, setChoosing] = useState(false);
  const [assetId, setAssetId] = useState("");
  return (
    <tr>
      <td><code>{e.hardwareId}</code></td>
      <td>{t(`devicesAdmin.status.${e.status}`, { defaultValue: e.status })}</td>
      <td>{e.assetId ?? "—"}</td>
      <td>{fmt(e.firstSeen, i18n.language)}</td>
      <td>{fmt(e.lastSeen, i18n.language)}</td>
      <td className={css.actions}>
        {e.status !== "approved" && !choosing && (
          <button className={css.linkBtn} onClick={() => { setAssetId(""); setChoosing(true); }}>
            {t("devicesAdmin.approve")}
          </button>
        )}
        {e.status !== "approved" && choosing && (
          <span className={css.actions}>
            <select value={assetId} onChange={(ev) => setAssetId(ev.target.value)}
                    aria-label={t("devicesAdmin.pickAsset")}>
              <option value="">{t("devicesAdmin.pickAsset")}</option>
              {freeAssets.map((a) => (
                <option key={a.id} value={a.id}>{a.label} ({a.id})</option>
              ))}
            </select>
            <button className={css.linkBtn} disabled={!assetId}
                    onClick={() => { onApprove(assetId); setChoosing(false); }}>
              {t("devicesAdmin.bind")}
            </button>
            <button className={css.linkBtn} onClick={() => setChoosing(false)}>
              {t("common.cancel")}
            </button>
            {freeAssets.length === 0 && (
              <span className={css.muted}>{t("devicesAdmin.noFreeAssets")}</span>
            )}
          </span>
        )}
        {e.status === "approved" && (
          <button className={css.dangerBtn} onClick={() => {
            if (confirm(t("devicesAdmin.confirmRevoke", { hw: e.hardwareId }))) onRevoke();
          }}>{t("devicesAdmin.revoke")}</button>
        )}
        {/* Reflashed-unit recovery (ADR 135). Offered on every status: a device reflashed
            while still pending is just as locked out (its new secret 403s on enroll). */}
        {!choosing && (
          <button className={css.dangerBtn} onClick={() => {
            if (confirm(t("devicesAdmin.confirmResetSecret", { hw: e.hardwareId }))) onResetSecret();
          }}>{t("devicesAdmin.resetSecret")}</button>
        )}
      </td>
    </tr>
  );
}

function fmt(iso: string, locale: string): string {
  try { return new Date(iso).toLocaleString(locale); } catch { return iso; }
}
